import React, { useState, useEffect } from 'react';
import { FaExternalLinkAlt, FaNewspaper } from 'react-icons/fa';

function LatestArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        // Relativer Pfad zur JSON-Datei (vermeidet CORS-Probleme zwischen www.tivoli12.at und tivoli12.at)
        const response = await fetch('/wp-content/uploads/mfcontentshow/mfcontentshow-entries.json');
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Artikel');
        }
        
        const data = await response.json();
        
        // Prüfe das neue Datenformat: {timestamp, total_entries, entries: []}
        let articlesArray = [];
        if (Array.isArray(data)) {
          // Fallback für altes Format (direktes Array)
          articlesArray = data;
        } else if (data && Array.isArray(data.entries)) {
          // Neues Format: Objekt mit entries-Array
          articlesArray = data.entries;
        } else {
          console.error('Unbekanntes Datenformat:', data);
          setError('Artikel-Datenformat ungültig');
          return;
        }
        
        // Sortiere nach "sorting" Feld (niedrigere Zahlen = neuer) oder nach ID (höhere = neuer)
        const sortedArticles = articlesArray.sort((a, b) => {
          if (a.sorting !== undefined && b.sorting !== undefined) {
            return a.sorting - b.sorting;
          }
          // Fallback: Sortiere nach ID (höhere ID = neuer)
          return parseInt(b.id) - parseInt(a.id);
        });
        
        // Nimm die ersten 4 Artikel
        setArticles(sortedArticles.slice(0, 4));
      } catch (err) {
        console.error('Fehler beim Laden der Artikel:', err);
        setError('Artikel konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const formatImageUrl = (url) => {
    // Stelle sicher, dass die URL korrekt formatiert ist
    return url ? url.replace(/\\/g, '') : null;
  };

  if (loading) {
    return (
      <div className="mb-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl text-white font-marker mb-2 flex items-center justify-center gap-3">
            <FaNewspaper className="text-primary" />
            Neueste Artikel
          </h2>
          <p className="text-white/80 text-lg">
            Die aktuellsten Beiträge von Tivoli12.at
          </p>
        </div>
        <div className="flex justify-center">
          <div className="modern-card p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-300">Lade Artikel...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-12">
        <div className="text-center">
          <div className="modern-card p-6">
            <div className="text-red-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl text-white font-marker mb-2 flex items-center justify-center gap-3">
          <FaNewspaper className="text-primary animate-pulse" />
          Neueste Artikel
        </h2>
        <p className="text-white/80 text-lg">
          Die aktuellsten Beiträge von Tivoli12.at
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {articles.map((article, index) => (
          <a
            key={article.id}
            href={article.link.replace(/\\/g, '')}
            target="_blank"
            rel="noopener noreferrer"
            className="group block h-full"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div
              className="modern-card flex flex-col justify-between h-[420px] group-hover:scale-[1.02] group-hover:shadow-2xl transition-all duration-300 overflow-hidden bg-gradient-to-br from-white via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 border-2 border-transparent"
              style={{ borderColor: 'transparent', boxShadow: '0 0 0 0 rgba(68,143,101,0)', transition: 'box-shadow 0.3s, border-color 0.3s' }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(68,143,101,0.25)';
                e.currentTarget.style.borderColor = 'rgb(68,143,101)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 0 0 0 rgba(68,143,101,0)';
                e.currentTarget.style.borderColor = 'transparent';
              }}
            >
              {/* Artikel Bild mit Overlay */}
              {article.image && (
                <div className="relative overflow-hidden h-52">
                  <img
                    src={formatImageUrl(article.image)}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.height = '140px';
                      e.target.parentElement.classList.add('bg-gradient-to-br', 'from-primary/20', 'to-secondary/20', 'flex', 'items-center', 'justify-center');
                      e.target.parentElement.innerHTML = `
                        <div class="text-center text-primary">
                          <div class="text-5xl mb-2">📰</div>
                          <div class="text-sm font-medium">Tivoli12.at</div>
                        </div>
                      `;
                    }}
                  />
                  {/* Stylischer Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                  {/* Hover-Effekt Icon */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                      <FaExternalLinkAlt style={{ color: 'rgb(68,143,101)' }} className="text-sm" />
                    </div>
                  </div>
                </div>
              )}

              {/* Artikel Content */}
              <div className="p-6 flex flex-col flex-1 justify-between">
                <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-4 line-clamp-2 group-hover:text-[rgb(68,143,101)] transition-colors duration-300 leading-snug">
                  {article.title}
                </h3>
                {/* Stylischer Read More Indicator */}
                <div className="flex items-center justify-end mt-auto">
                  <div className="text-[rgb(68,143,101)] group-hover:text-[rgb(68,143,101)] transition-colors duration-300">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span>Weiterlesen</span>
                      <div className="w-8 h-px bg-current transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                      <FaExternalLinkAlt style={{ color: 'rgb(68,143,101)' }} className="text-xs transform translate-x-0 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Subtiler Bottom Accent */}
              <div className="h-1 bg-gradient-to-r from-[rgb(68,143,101)] via-[rgb(68,143,101)] to-[rgb(68,143,101)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center"></div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default LatestArticles;
